import { ArrowUpRight, Download, Megaphone, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  currentAppVersion,
  type PlatformAnnouncement,
  type PlatformUpdateNotice,
  updateIsAvailable,
  updateIsRequired,
} from "../platform-bootstrap";
import { useI18n } from "../i18n";

export function PlatformNotices({
  update,
  announcements,
}: {
  update?: PlatformUpdateNotice;
  announcements: PlatformAnnouncement[];
}) {
  const { t } = useI18n();
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const eligibleAnnouncements = useMemo(
    () =>
      announcements.filter((announcement) => {
        if (
          announcement.versions?.length &&
          !announcement.versions.includes(currentAppVersion())
        )
          return false;
        return (
          announcement.display === "always" ||
          localStorage.getItem(`orni.announcement.${announcement.id}`) !== "seen"
        );
      }),
    [announcements],
  );
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  useEffect(() => setAnnouncementIndex(0), [eligibleAnnouncements.length]);
  const announcement = eligibleAnnouncements[announcementIndex];
  const required = update ? updateIsRequired(update) : false;
  const showUpdate = Boolean(
    update && updateIsAvailable(update) && (required || !updateDismissed),
  );
  if (!showUpdate && !announcement) return null;
  const closeAnnouncement = () => {
    if (!announcement) return;
    if (announcement.display === "once")
      localStorage.setItem(`orni.announcement.${announcement.id}`, "seen");
    setAnnouncementIndex((index) => index + 1);
  };
  return (
    <div className="platform-notice-backdrop" role="presentation">
      {showUpdate && update ? (
        <section
          className="platform-notice"
          role="dialog"
          aria-modal={required}
          aria-label={t("platform.update.aria")}
        >
          <i><Download size={20} /></i>
          <div>
            <span>{required ? t("platform.update.required") : t("platform.update.available")}</span>
            <h2>{update.title ?? `OrniWork ${update.latestVersion}`}</h2>
            <p>{update.detail ?? t("platform.update.fallback")}</p>
          </div>
          <footer>
            {!required ? (
              <button type="button" onClick={() => setUpdateDismissed(true)}>
                {t("common.later")}
              </button>
            ) : null}
            <a href={update.downloadUrl} target="_blank" rel="noreferrer">
              {t("platform.update.action")} <ArrowUpRight size={14} />
            </a>
          </footer>
        </section>
      ) : announcement ? (
        <section className="platform-notice" role="dialog" aria-label={t("platform.announcement.aria")}>
          <i><Megaphone size={20} /></i>
          <button className="platform-notice-close" type="button" onClick={closeAnnouncement} aria-label={t("common.close")}>
            <X size={16} />
          </button>
          <div>
            <span>{t("platform.announcement.label")}</span>
            <h2>{announcement.title}</h2>
            <p>{announcement.detail}</p>
          </div>
          <footer>
            <button type="button" onClick={closeAnnouncement}>{t("common.gotIt")}</button>
            {announcement.actionUrl ? (
              <a href={announcement.actionUrl} target="_blank" rel="noreferrer">
                {announcement.actionLabel ?? t("platform.announcement.details")} <ArrowUpRight size={14} />
              </a>
            ) : null}
          </footer>
        </section>
      ) : null}
    </div>
  );
}
